import React from "react";
import { Star } from "lucide-react";

/**
 * Trust Score 1-5 stars badge.
 *
 * Renders 5 star outlines, with `score` of them filled gold/amber.
 * Hovering / focusing shows the component-level breakdown tooltip
 * (league hit-rate, calibration gap, sample size).
 *
 * @param {number} score      integer 1..5 (gracefully no-ops if missing)
 * @param {object} components { league_hit_rate_pct, league_n,
 *                              bucket_calibration_gap_pp, bucket_n,
 *                              sample_size_score }
 * @param {string} testId     optional data-testid for the wrapper
 * @param {string} size       "sm" (default) | "xs"
 */
export const TrustStars = ({ score, components, testId, size = "sm" }) => {
  if (typeof score !== "number" || score < 1) return null;

  const stars = Math.max(1, Math.min(5, Math.round(score)));
  const dim = size === "xs" ? 10 : 12;
  const gap = size === "xs" ? "gap-[1px]" : "gap-0.5";

  const label = `${stars}/5 Trust`;

  const tooltip = components
    ? [
        components.league_hit_rate_pct != null
          ? `League ${components.league_hit_rate_pct}% (n=${components.league_n})`
          : null,
        components.bucket_calibration_gap_pp != null
          ? `Bucket gap ${components.bucket_calibration_gap_pp}pp (n=${components.bucket_n})`
          : null,
        components.sample_size_score != null
          ? `Sample weight ${components.sample_size_score}`
          : null,
      ]
        .filter(Boolean)
        .join(" · ")
    : "";

  const titleAttr = tooltip ? `${label} — ${tooltip}` : label;

  return (
    <span
      className={`inline-flex items-center ${gap} shrink-0`}
      title={titleAttr}
      aria-label={titleAttr}
      data-testid={testId}
    >
      {[1, 2, 3, 4, 5].map((n) => {
        const filled = n <= stars;
        return (
          <Star
            key={n}
            size={dim}
            strokeWidth={2}
            className={
              filled
                ? "fill-amber-400 text-amber-400 drop-shadow-[0_0_3px_rgba(251,191,36,0.45)]"
                : "fill-transparent text-amber-400/30"
            }
          />
        );
      })}
    </span>
  );
};

export default TrustStars;
